import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuickCreate } from "@/lib/quickCreateContext";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { FileStack, Search, Plus, Loader2, Edit2, Trash2 } from "lucide-react";
import { api, ProjectTemplate, ProjectTemplateInput } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";

export default function Templates() {
  const [, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { trigger, setTrigger } = useQuickCreate();
  useEffect(() => {
    if (trigger === "new-template") { setDialogOpen(true); setTrigger(null); }
  }, [trigger]);
  const [formData, setFormData] = useState<ProjectTemplateInput>({
    title: "",
    description: "",
    default_duration_days: undefined,
    recurring_pattern: undefined,
    default_billing_rate: undefined,
    is_active: true,
  });
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading } = useQuery<ProjectTemplate[]>({
    queryKey: ["projectTemplates", statusFilter],
    queryFn: () => api.getProjectTemplates({
      status: statusFilter !== "all" ? statusFilter : undefined,
    }),
  });

  const createMutation = useMutation({
    mutationFn: (data: ProjectTemplateInput) => api.createProjectTemplate(data),
    onSuccess: (template) => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplates"] });
      toast({ title: "Template created successfully" });
      closeDialog();
      navigate(`/templates/${template.id}`);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteProjectTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplates"] });
      toast({ title: "Template deleted" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setFormData({
      title: "",
      description: "",
      default_duration_days: undefined,

      default_billing_rate: undefined,
      is_active: true,
    });
  };

  const openCreateDialog = () => {
    setFormData({
      title: "",
      description: "",
      default_duration_days: undefined,

      default_billing_rate: undefined,
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast({ title: "Error", description: "Title is required", variant: "destructive" });
      return;
    }
    createMutation.mutate(formData);
  };

  const filteredTemplates = templates.filter(template => 
    (template.title ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (template.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <FileStack className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Project Templates</h1>
              <p className="text-muted-foreground text-sm">
                Manage reusable templates for projects
              </p>
            </div>
          </div>
          <Button onClick={openCreateDialog} data-testid="button-create-template">
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-templates"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="select-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

            </div>
          </CardHeader>
          <Separator />
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {searchQuery ? "No templates match your search" : "No templates found. Create your first template!"}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-center">Tasks</TableHead>
                    <TableHead className="text-center">Duration</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map((template) => (
                    <TableRow
                      key={template.id}
                      className="hover:bg-muted/50"
                      data-testid={`row-template-${template.id}`}
                    >
                      <TableCell className="font-medium">
                        <a
                          href={`/templates/${template.id}`}
                          className="block no-underline text-inherit hover:underline"
                        >
                          {template.title}
                        </a>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[300px] truncate">
                        {template.description || "—"}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{template.task_count || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {template.default_duration_days ? `${template.default_duration_days} days` : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={template.is_active ? "default" : "secondary"}>
                          {template.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                            data-testid={`button-edit-template-${template.id}`}
                          >
                            <a href={`/templates/${template.id}`}>
                              <Edit2 className="h-4 w-4" />
                            </a>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              if (await askConfirm({
                                title: "Delete this template?",
                                subject: "template",
                                name: template.title,
                                body: "will be deleted.",
                                confirmLabel: "Delete",
                                destructive: true,
                              })) {
                                deleteMutation.mutate(template.id);
                              }
                            }}
                            data-testid={`button-delete-template-${template.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Project Template</DialogTitle>
            <DialogDescription>
              Create a reusable template for projects. You can add tasks after creating it.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Annual Tax Return"
                data-testid="input-template-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ""}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this template..."
                rows={3}
                data-testid="input-template-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Default Duration (days)</Label>
                <Input
                  id="duration"
                  type="number"
                  min={1}
                  value={formData.default_duration_days || ""}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    default_duration_days: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                  placeholder="30"
                  data-testid="input-template-duration"
                />
              </div>

            </div>

            <div className="space-y-2">
              <Label htmlFor="billing_rate">Default Billing Rate ($/hr)</Label>
              <Input
                id="billing_rate"
                type="number"
                min={0}
                step={0.01}
                value={formData.default_billing_rate || ""}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  default_billing_rate: e.target.value ? parseFloat(e.target.value) : undefined 
                })}
                placeholder="150.00"
                data-testid="input-template-billing-rate"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="button-cancel-template">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              data-testid="button-save-template"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
